import { Component, computed, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MicaChatApiService } from '@core/services/api/mica-chat-api.service';
import { ToastService } from '@shared/services/toast.service';
import {
  IFlujoWhatsapp,
  IPasoFlujo,
  ICrearPasoReq,
  IEditarPasoReq,
  IPasoFlujoContenido,
  IPasoOpcion,
} from '@core/interfaces/request-response/flujo.dto.interface';

@Component({
  selector: 'app-mica-chat-flujo-editor',
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './mica-chat-flujo-editor.html',
})
export class MicaChatFlujoEditor implements OnInit {
  private readonly api = inject(MicaChatApiService);
  private readonly toast = inject(ToastService);

  flujo = input.required<IFlujoWhatsapp>();
  cerrar = output<void>();

  pasos = signal<IPasoFlujo[]>([]);
  pasoEditando = signal<IPasoFlujo | null>(null);

  pasosPorId = computed(() => {
    const map: Record<number, string> = {};
    for (const p of this.pasos()) {
      map[p.id] = p.titulo || `Paso ${p.orden + 1}`;
    }
    return map;
  });

  formTitulo = signal('');
  formMensaje = signal('');
  formPalabras = signal('');
  formMediaUrl = signal('');
  formMediaNombre = signal('');
  formMediaTipo = signal<'image' | 'document'>('image');
  subiendoArchivo = signal(false);
  formOpciones = signal<IPasoOpcion[]>([]);
  formOpcionNumero = signal('');
  formOpcionTexto = signal('');
  formOpcionDestino = signal<number>(0);
  esNuevo = signal(true);

  ngOnInit() {
    this.pasos.set([...(this.flujo().pasos || [])].sort((a, b) => a.orden - b.orden));
  }

  recargar() {
    this.api.obtenerFlujo(this.flujo().id).subscribe({
      next: (f) => {
        this.pasos.set([...(f.pasos || [])].sort((a, b) => a.orden - b.orden));
      },
    });
  }

  contenidoResumen(paso: IPasoFlujo): string {
    const c = paso.contenido;
    if (!c) return '';
    const partes: string[] = [];
    if (c.palabras_clave?.length) partes.push(`🔑 ${c.palabras_clave.join(', ')}`);
    if (c.mensaje) partes.push(`💬 ${c.mensaje.substring(0, 50)}`);
    if (c.opciones?.length) partes.push(`📋 ${c.opciones.length} opciones`);
    return partes.join(' · ').substring(0, 80);
  }

  abrirNuevo() {
    this.pasoEditando.set({
      id: 0, flujo_id: this.flujo().id, orden: this.pasos().length,
      tipo: 'MENSAJE', titulo: null, contenido: null,
      created_at: '', updated_at: '',
    });
    this.formTitulo.set('');
    this.formMensaje.set('');
    this.formPalabras.set('');
    this.formMediaUrl.set('');
    this.formMediaNombre.set('');
    this.formMediaTipo.set('image');
    this.formOpciones.set([]);
    this.formOpcionNumero.set('');
    this.formOpcionTexto.set('');
    this.formOpcionDestino.set(0);
    this.esNuevo.set(true);
  }

  abrirEditar(paso: IPasoFlujo) {
    this.pasoEditando.set({ ...paso });
    const c = paso.contenido || {};
    this.formTitulo.set(paso.titulo || '');
    this.formMensaje.set(c.mensaje || '');
    this.formPalabras.set((c.palabras_clave || []).join(', '));
    this.formMediaUrl.set(c.media?.url || '');
    this.formMediaTipo.set(c.media?.tipo || 'image');
    this.formMediaNombre.set(c.media?.nombre || '');
    this.formOpciones.set(c.opciones || []);
    this.formOpcionNumero.set('');
    this.formOpcionTexto.set('');
    this.formOpcionDestino.set(0);
    this.esNuevo.set(false);
  }

  cerrarFormulario() {
    this.pasoEditando.set(null);
  }

  guardar() {
    const contenido = this.buildContenido();

    if (this.esNuevo()) {
      const dto: ICrearPasoReq = { tipo: 'MENSAJE', titulo: this.formTitulo() || undefined, contenido };
      this.api.agregarPaso(this.flujo().id, dto).subscribe({
        next: () => {
          this.toast.show('top-right', 'success', 'Agregado', 'Paso creado');
          this.cerrarFormulario();
          this.recargar();
        },
        error: (err: any) => this.toast.show('top-right', 'error', 'Error', err.error?.message || ''),
      });
    } else {
      const p = this.pasoEditando()!;
      const dto: IEditarPasoReq = { tipo: 'MENSAJE', titulo: this.formTitulo() || undefined, contenido };
      this.api.editarPaso(this.flujo().id, p.id, dto).subscribe({
        next: () => {
          this.toast.show('top-right', 'success', 'Guardado', 'Paso actualizado');
          this.cerrarFormulario();
          this.recargar();
        },
        error: (err: any) => this.toast.show('top-right', 'error', 'Error', err.error?.message || ''),
      });
    }
  }

  eliminarPaso(paso: IPasoFlujo) {
    this.api.eliminarPaso(this.flujo().id, paso.id).subscribe({
      next: () => { this.toast.show('top-right', 'success', 'Eliminado', ''); this.recargar(); },
      error: (err: any) => this.toast.show('top-right', 'error', 'Error', err.error?.message || ''),
    });
  }

  agregarOpcion() {
    const num = this.formOpcionNumero().trim();
    const txt = this.formOpcionTexto().trim();
    if (!num || !txt) return;
    const destinoId = this.formOpcionDestino() > 0 ? this.formOpcionDestino() : null;
    this.formOpciones.update(prev => [...prev, { numero: num, texto: txt, paso_destino_id: destinoId }]);
    this.formOpcionNumero.set('');
    this.formOpcionTexto.set('');
    this.formOpcionDestino.set(0);
  }

  quitarOpcion(idx: number) {
    this.formOpciones.update(prev => prev.filter((_, i) => i !== idx));
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.toast.show('top-right', 'error', 'Error', 'El archivo excede 10 MB');
      return;
    }

    this.subiendoArchivo.set(true);
    const formData = new FormData();
    formData.append('archivo', file);

    this.api.subirFlujoMedia(formData).subscribe({
      next: (httpEvent: any) => {
        if (httpEvent.type === 4 && httpEvent.body) {
          this.formMediaUrl.set(httpEvent.body.url);
          this.formMediaNombre.set(httpEvent.body.fileName || file.name);
          const isImage = file.type.startsWith('image/');
          this.formMediaTipo.set(isImage ? 'image' : 'document');
          this.subiendoArchivo.set(false);
          this.toast.show('top-right', 'success', 'Subido', 'Archivo listo');
        }
      },
      error: (err: any) => {
        this.subiendoArchivo.set(false);
        this.toast.show('top-right', 'error', 'Error', err.error?.message || 'Error al subir');
      },
    });
  }

  moverPaso(idx: number, direccion: 'arriba' | 'abajo') {
    const lista = [...this.pasos()];
    const newIdx = direccion === 'arriba' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= lista.length) return;
    [lista[idx], lista[newIdx]] = [lista[newIdx], lista[idx]];
    this.pasos.set(lista);
    this.api.reordenarPasos(this.flujo().id, lista.map((p, i) => ({ id: p.id, orden: i }))).subscribe({
      error: (err: any) => this.toast.show('top-right', 'error', 'Error', err.error?.message || ''),
    });
  }

  private buildContenido(): IPasoFlujoContenido {
    const mediaUrl = this.formMediaUrl().trim();
    return {
      mensaje: this.formMensaje(),
      palabras_clave: this.formPalabras().split(',').map(w => w.trim()).filter(Boolean),
      ...(this.formOpciones().length ? { opciones: this.formOpciones() } : {}),
      ...(mediaUrl ? { media: { url: mediaUrl, tipo: this.formMediaTipo(), nombre: this.formMediaNombre() || undefined } } : {}),
    };
  }
}
